'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useTranslation } from '@/i18n/client'
import {
  AwardLegalShell,
  type LegalArticle,
} from '@/components/award/legal-shell'

// ═══════════════════════════════════════════════════════════════
// FAQ-Daten — 44 Fragen (mit optionaler Kategorie fuer internen
// Gebrauch + JSON-LD). Alle Q/A-Texte kommen aus i18n.
// ═══════════════════════════════════════════════════════════════

type CategoryKey =
  | 'grundlagen' | 'wertung' | 'igc' | 'polaren'
  | 'app' | 'pruefung' | 'plans' | 'recht'

interface FaqEntry {
  code: string   // "01" .. "44"
  qKey: string
  aKey: string
  category: CategoryKey
  aiDisclaimer?: boolean
}

const FAQS: FaqEntry[] = [
  { code: '01', qKey: 'faq.whatIsFgl', aKey: 'faq.whatIsFglA', category: 'grundlagen' },
  { code: '02', qKey: 'faq.isFree', aKey: 'faq.isFreeA', category: 'grundlagen' },
  { code: '03', qKey: 'pricing.faqUpgrade', aKey: 'pricing.faqUpgradeA', category: 'plans' },
  { code: '04', qKey: 'pricing.faqDonator', aKey: 'pricing.faqDonatorA', category: 'plans' },
  { code: '05', qKey: 'faq.dataProtection', aKey: 'faq.dataProtectionA', category: 'grundlagen' },
  { code: '06', qKey: 'faq.whatIsValid', aKey: 'faq.whatIsValidA', category: 'wertung' },
  { code: '07', qKey: 'pricing.faqBulk', aKey: 'pricing.faqBulkA', category: 'plans' },
  { code: '08', qKey: 'faq.whatIsIgc', aKey: 'faq.whatIsIgcA', category: 'igc' },
  { code: '09', qKey: 'faq.classification', aKey: 'faq.classificationA', category: 'wertung' },
  { code: '10', qKey: 'faq.slopeVsFlat', aKey: 'faq.slopeVsFlatA', category: 'wertung' },
  { code: '11', qKey: 'pricing.faqThermal', aKey: 'pricing.faqThermalA', category: 'plans' },
  { code: '12', qKey: 'faq.support', aKey: 'faq.supportA', category: 'grundlagen' },
  { code: '13', qKey: 'faq.whatIsFreeGlide', aKey: 'faq.whatIsFreeGlideA', category: 'wertung' },
  { code: '14', qKey: 'faq.isOpenSource', aKey: 'faq.isOpenSourceA', category: 'grundlagen' },
  { code: '15', qKey: 'faq.howManyLoggers', aKey: 'faq.howManyLoggersA', category: 'igc' },
  { code: '16', qKey: 'faq.rcElectronicsRisk', aKey: 'faq.rcElectronicsRiskA', category: 'igc' },
  { code: '17', qKey: 'faq.dsgvoArt20Migration', aKey: 'faq.dsgvoArt20MigrationA', category: 'recht', aiDisclaimer: true },
  { code: '18', qKey: 'faq.q18', aKey: 'faq.q18A', category: 'pruefung' },
  { code: '19', qKey: 'faq.q19', aKey: 'faq.q19A', category: 'pruefung' },
  { code: '20', qKey: 'faq.q20', aKey: 'faq.q20A', category: 'wertung' },
  { code: '21', qKey: 'faq.q21', aKey: 'faq.q21A', category: 'wertung' },
  { code: '22', qKey: 'faq.q22', aKey: 'faq.q22A', category: 'wertung' },
  { code: '23', qKey: 'faq.q23', aKey: 'faq.q23A', category: 'wertung' },
  { code: '24', qKey: 'faq.q24', aKey: 'faq.q24A', category: 'wertung' },
  { code: '25', qKey: 'faq.q25', aKey: 'faq.q25A', category: 'wertung' },
  { code: '26', qKey: 'faq.q26', aKey: 'faq.q26A', category: 'polaren' },
  { code: '27', qKey: 'faq.q27', aKey: 'faq.q27A', category: 'polaren' },
  { code: '28', qKey: 'faq.q28', aKey: 'faq.q28A', category: 'polaren' },
  { code: '29', qKey: 'faq.q29', aKey: 'faq.q29A', category: 'polaren' },
  { code: '30', qKey: 'faq.q30', aKey: 'faq.q30A', category: 'polaren' },
  { code: '31', qKey: 'faq.q31', aKey: 'faq.q31A', category: 'polaren' },
  { code: '32', qKey: 'faq.q32', aKey: 'faq.q32A', category: 'plans' },
  { code: '33', qKey: 'faq.q33', aKey: 'faq.q33A', category: 'app' },
  { code: '34', qKey: 'faq.q34', aKey: 'faq.q34A', category: 'app' },
  { code: '35', qKey: 'faq.q35', aKey: 'faq.q35A', category: 'app' },
  { code: '36', qKey: 'faq.q36', aKey: 'faq.q36A', category: 'pruefung' },
  { code: '37', qKey: 'faq.q37', aKey: 'faq.q37A', category: 'pruefung' },
  { code: '38', qKey: 'faq.q38', aKey: 'faq.q38A', category: 'pruefung' },
  { code: '39', qKey: 'faq.q39', aKey: 'faq.q39A', category: 'wertung' },
  { code: '40', qKey: 'faq.q40', aKey: 'faq.q40A', category: 'recht', aiDisclaimer: true },
  { code: '41', qKey: 'faq.q41', aKey: 'faq.q41A', category: 'igc', aiDisclaimer: true },
  { code: '42', qKey: 'faq.q42', aKey: 'faq.q42A', category: 'igc' },
  { code: '43', qKey: 'faq.q43', aKey: 'faq.q43A', category: 'igc', aiDisclaimer: true },
  { code: '44', qKey: 'faq.q44', aKey: 'faq.q44A', category: 'igc' },
]

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════

/**
 * Wandelt Inline-Markdown-Links [Label](/route) in echte Next-Links um.
 */
function renderWithLinks(input: string): React.ReactNode {
  if (!input.includes('](/')) return input
  const re = /\[([^\]]+)\]\((\/[a-zA-Z0-9._~\-/]*)\)/g
  const out: React.ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(input)) !== null) {
    if (m.index > last) out.push(input.slice(last, m.index))
    const [, label, href] = m
    out.push(<Link key={`l-${m.index}`} href={href} className="award-link">{label}</Link>)
    last = m.index + m[0].length
  }
  if (last < input.length) out.push(input.slice(last))
  return <>{out}</>
}

/**
 * Rendert eine FAQ-Antwort. Erkennt Brief-Vorlagen (umschlossen von
 * typografischen „...") und stellt sie mit Copy-Button dar.
 */
function FaqAnswer({ text }: { text: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const open = '„'
  const close = '"'
  const start = text.indexOf(open)
  const end = start >= 0 ? text.indexOf(close, start + 1) : -1

  if (start < 0 || end < 0) {
    return <p style={{ whiteSpace: 'pre-line' }}>{renderWithLinks(text)}</p>
  }

  const before = text.slice(0, start)
  const letter = text.slice(start + 1, end).trim()
  const after = text.slice(end + 1)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(letter)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = letter
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch {}
      document.body.removeChild(textarea)
    }
  }

  return (
    <>
      {before && <p style={{ whiteSpace: 'pre-line' }}>{renderWithLinks(before)}</p>}
      <div className="faq-letter-box">
        <button
          type="button"
          onClick={handleCopy}
          className="faq-letter-copy"
          aria-label={t('faq.letterCopyAria')}
        >
          {copied ? t('faq.letterCopyDone') : t('faq.letterCopyAction')}
        </button>
        <pre className="faq-letter-text">{letter}</pre>
      </div>
      {after && <p style={{ whiteSpace: 'pre-line' }}>{renderWithLinks(after)}</p>}
      <style jsx global>{`
        .faq-letter-box {
          position: relative;
          margin: 14px 0;
          padding: 18px 18px 18px 18px;
          border: 1px solid var(--award-line-hi);
          border-radius: 12px;
          background: color-mix(in oklch, var(--award-ink) 60%, transparent);
        }
        .faq-letter-text {
          margin: 0;
          padding-right: 90px;
          font-family: var(--font-sans);
          font-size: 13px;
          line-height: 1.7;
          color: var(--award-cirrus);
          white-space: pre-wrap;
          word-break: break-word;
          overflow-wrap: anywhere;
          user-select: text;
          cursor: text;
        }
        .faq-letter-copy {
          position: absolute;
          top: 12px;
          right: 12px;
          padding: 6px 12px;
          font-family: var(--font-sans);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.05em;
          color: var(--award-gold);
          background: color-mix(in oklch, var(--award-gold) 10%, transparent);
          border: 1px solid color-mix(in oklch, var(--award-gold) 35%, transparent);
          border-radius: 8px;
          cursor: pointer;
          transition: all .2s ease;
          z-index: 2;
        }
        .faq-letter-copy:hover {
          background: color-mix(in oklch, var(--award-gold) 18%, transparent);
          border-color: var(--award-gold);
          transform: translateY(-1px);
        }
      `}</style>
    </>
  )
}

/**
 * AI-Disclaimer-Box unter der Antwort bei rechts-/hersteller-relevanten Fragen.
 */
function AiDisclaimer() {
  const { t } = useTranslation()
  return (
    <p
      style={{
        marginTop: '16px',
        padding: '12px 16px',
        border: '1.5px solid var(--state-danger, #dc2626)',
        borderRadius: '8px',
        background: 'var(--state-danger-soft, rgba(220,38,38,0.10))',
        fontSize: '13px',
        fontWeight: 700,
        color: 'var(--state-danger, #dc2626)',
        lineHeight: 1.5,
      }}
    >
      {t('faq.aiDisclaimer')}
    </p>
  )
}

// ═══════════════════════════════════════════════════════════════
// HAUPT-SEITE — nutzt AwardLegalShell wie /wettbewerbsordnung
// ═══════════════════════════════════════════════════════════════

export default function FaqPage() {
  const { t } = useTranslation()

  // Alle Fragen sortiert 01-44 als LegalArticle
  const articles: LegalArticle[] = [...FAQS]
    .sort((a, b) => a.code.localeCompare(b.code))
    .map((f) => ({
      num: `${t('faq.pageEyebrow').split('·')[0].trim() || 'FAQ'} ${f.code}`,
      numDisplay: f.code,
      title: t(f.qKey),
      body: (
        <>
          <FaqAnswer text={t(f.aKey)} />
          {f.aiDisclaimer && <AiDisclaimer />}
        </>
      ),
    }))

  // JSON-LD FAQPage-Schema fuer ChatGPT/Perplexity/Gemini/Copilot
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [...FAQS]
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((f) => ({
        '@type': 'Question',
        name: t(f.qKey),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t(f.aKey).slice(0, 4000),
        },
      })),
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <AwardLegalShell
        eyebrow={t('faq.pageEyebrow')}
        meta={t('faq.answersCount', { count: FAQS.length })}
        title={`${t('faq.heroTitle1')} ${t('faq.heroTitleAccent')}`}
        gradWord={t('faq.heroTitleAccent')}
        lead={t('faq.heroSubtitle')}
        articles={articles}
        closingEyebrow={t('faq.closingEyebrow')}
        closingTitle={
          <>
            {t('faq.closingTitle1')}{' '}
            <span className="award-grad-text">{t('faq.closingTitleAccent')}</span>{' '}
            {t('faq.closingTitle2')}
          </>
        }
        primaryCta={{ href: '/register', label: t('faq.closingCtaRegister') }}
        secondaryCta={{ href: 'mailto:info@modellpilot.eu', label: 'info@modellpilot.eu' }}
      />
    </>
  )
}
